import type { InventoryItem } from 'types/shop-bff/types';

type AttributeKeyType = Extract<'attributes', keyof InventoryItem>;
type PickedAttribute = Pick<
  InventoryItem[AttributeKeyType][number],
  'name' | 'value'
>;
type PickedAttributeContainer = {
  [key in AttributeKeyType]: Array<PickedAttribute>;
};

/**
 * Returns a filtered array of attributes from `test` if they match attributes from `current`
 * e.g.
 *
 * current = {attributes:[{name:"a", value:"a"}, {name:"b", value:"a"}]}
 * test = {attributes:[{name:"a", value:"a"}, {name:"b", value:"b"}]}
 * findSomeMatchingAttributes(current,test) === [{name:"a", value:"a"}]
 */
const findSomeMatchingAttributes: (
  current: PickedAttributeContainer,
  test: PickedAttributeContainer
) => PickedAttribute[] = (current, test) => {
  return current.attributes.filter(({ name, value }) => {
    return test.attributes.some(
      ({ name: testName, value: testValue }) =>
        name === testName && value === testValue
    );
  });
};

const areAttributesCompatible: (
  current: PickedAttributeContainer,
  test: PickedAttributeContainer
) => boolean = (current, test) => {
  if (current.attributes.length !== test.attributes.length) return false;

  const matchingAttributeCount = findSomeMatchingAttributes(
    current,
    test
  ).length;
  const oneOrFewerAttributeDifference =
    matchingAttributeCount >= current.attributes.length - 1;

  return oneOrFewerAttributeDifference;
};

/**
 * Returns items from `set` if they have compatible attributes with `current`
 * attribute compatibility is determined when:
 * for attributes of current, `Ca`, and attributes for set item, `Sa`
 *
 * `Ca` is compatible with `Sa` if
 *
 * `Ca.length` === `Sa.length`
 * AND
 * ( `Ca` ∩ `Sa` ) <= ( `Ca.length` - 1 )
 *
 * ( if `Sa` has `Ca.length`-1 equal attributes )
 * ( if the attributes for set item have a difference in equality of attributes
 * when compared to the current item )
 */
export function findCompatibleInventoryItems<
  T extends PickedAttributeContainer,
>(current: T, set: T[]): T[] {
  return set.filter((sub) => areAttributesCompatible(current, sub));
}
